/**
 * Mana Locals Admin — M44 content & media boundary schemas.
 *
 * CONTRACT SOURCE (API repository): M44 content-media,
 * `src/domains/m44-content-media/contracts/content-media.schemas.ts`.
 *
 * ---------------------------------------------------------------------------
 * THE UPLOAD IS A THREE-STEP CONVERSATION
 * ---------------------------------------------------------------------------
 * M44 never accepts bytes on its own routes:
 *
 *   1. `POST /content/media-assets` creates the asset and returns an
 *      `uploadTarget` — a short-lived, presigned PUT URL. The bytes go
 *      STRAIGHT to the object store, never through the API.
 *   2. The caller PUTs the bytes to that URL.
 *   3. `POST /content/media-assets/{id}/upload-completion` tells the API the
 *      bytes are there; the API observes the object itself and settles the row.
 *
 * The consequence for the console: step 2 is a request to a DIFFERENT ORIGIN
 * with the file's own content type, and it must not carry the session cookie.
 * That is why the byte transfer lives in the module service and not in the
 * shared JSON transport.
 */
import { z } from 'zod';

import { CollectionResponseSchema, IsoDateTimeSchema, ResponseMetaSchema, UuidSchema } from '@/api/schemas/common';

/** The mime types M44's contract accepts. */
export const MediaMimeTypeSchema = z.enum([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
  'image/gif',
  'application/pdf',
  'video/mp4',
  'video/webm',
  'audio/mpeg',
  'text/plain',
  'text/markdown',
]);

export const MediaAssetStatusSchema = z.enum([
  'awaiting_upload',
  'scan_pending',
  'available',
  'quarantined',
  'scan_failed',
  'upload_expired',
]);

export const MediaAssetSchema = z
  .object({
    mediaAssetId: UuidSchema,
    ownerId: UuidSchema,
    mimeType: MediaMimeTypeSchema,
    byteSize: z.number().int().min(1).nullable(),
    contentHashSha256: z.string().nullable(),
    status: MediaAssetStatusSchema,
    rightsState: z.enum(['unlicensed', 'cleared', 'expired']),
    publicationState: z.enum(['none', 'published', 'revoked']),
    publiclyAccessible: z.boolean(),
    uploadExpiresAt: IsoDateTimeSchema.nullable(),
    uploadedAt: IsoDateTimeSchema.nullable(),
    scannedAt: IsoDateTimeSchema.nullable(),
    publishedAt: IsoDateTimeSchema.nullable(),
    version: z.number().int().min(1),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    /** Present only on the create response, where it carries the upload URL. */
    uploadTarget: z.object({ url: z.string().min(1), expiresAt: IsoDateTimeSchema }).optional(),
  })
  .passthrough();

export const MediaAssetListResponseSchema = CollectionResponseSchema(MediaAssetSchema);

export const MediaAssetResponseSchema = z.object({
  data: MediaAssetSchema,
  meta: ResponseMetaSchema,
});

export type MediaAsset = z.infer<typeof MediaAssetSchema>;
export type MediaMimeType = z.infer<typeof MediaMimeTypeSchema>;
export type MediaAssetStatus = z.infer<typeof MediaAssetStatusSchema>;
